// ============================================================
// مغز سهمیه‌ی خبازی‌ها
//
// صفحه‌ی سهمیه برای هر خبازی یک ردیف در هر دوره دارد:
//
//   جهانگیر علی نیا | 840297 | دوره خبازی مهر 1405 | 3.3 | دلفان | سنگک
//
// کاربر ولی به زبان آدمیزاد می‌پرسد «سهمیه‌ی جهانگیر چقدره؟» و
// انتظار دارد *همه‌ی ماه‌ها* را با هم بشنود و بفهمد کم شده یا زیاد.
// این فایل همان تبدیل است: ردیف‌های پراکنده → یک روایت ماه‌به‌ماه.
//
// سه کار می‌کند که در خود API نیست:
//   ۱) مرتب کردن دوره‌ها به ترتیب تقویمی (API ترتیبی ندارد و
//      «مهر» رشته است، نه عدد)
//   ۲) حساب کردن تغییر هر ماه نسبت به ماه قبل
//   ۳) وقتی نامی پیدا نشد، نزدیک‌ترین نام‌ها را پیشنهاد می‌دهد —
//      در گفتگوی صوتی، تشخیص غلطِ اسم قاعده است نه استثنا
// ============================================================

import Api from "Common/Components/ApiInterceptor/Api";
import { normalize, toPersianDigits } from "./text";

const ENDPOINT = "/monitoring/bakery-quotas/";
const API_PAGE_SIZE = 100;
const MAX_PAGES = 10;

/**
 * واحد سهمیه.
 *
 * ستون صفحه فقط «سهمیه» است و خود API واحدی برنمی‌گرداند. بنا را
 * بر «کیسه» گذاشتیم چون کاربر با همین واژه می‌پرسد. اگر واحد واقعی
 * چیز دیگری است، فقط همین یک رشته باید عوض شود.
 */
export const QUOTA_UNIT = "کیسه";

const JALALI_MONTHS = [
    "فروردین", "اردیبهشت", "خرداد", "تیر", "مرداد", "شهریور",
    "مهر", "آبان", "آذر", "دی", "بهمن", "اسفند",
];

export interface QuotaRow {
    buyer_name: string;
    buyer_code: string;
    city: string;
    baking_type: string;
    quota: number;
    distribution_period: string;
}

export interface MonthQuota {
    /** متن اصلی دوره، همان که در جدول می‌آید */
    period: string;
    label: string;
    year: number;
    month: number;
    quota: number;
    /** اختلاف با ماه قبلِ همین خبازی؛ برای اولین ماه undefined */
    delta?: number;
}

export interface BakerQuota {
    name: string;
    code: string;
    city: string;
    bakingType: string;
    months: MonthQuota[];
}

// ---------- خواندن دوره ----------

/**
 * «دوره خبازی مهر 1405» → { month: 7, year: 1405 }
 *
 * بدون این، مرتب‌سازی الفبایی می‌شد و «آبان» قبل از «مهر» می‌آمد.
 */
export const parsePeriod = (
    period: string
): { month: number; year: number; label: string } | null => {
    const text = normalize(period);
    if (!text) return null;

    let month = 0;
    for (let i = 0; i < JALALI_MONTHS.length; i++) {
        if (text.indexOf(normalize(JALALI_MONTHS[i])) !== -1) {
            month = i + 1;
            break;
        }
    }
    if (!month) return null;

    const yearMatch = text.match(/(1[34]\d{2})/);
    const year = yearMatch ? Number(yearMatch[1]) : 0;

    return { month, year, label: JALALI_MONTHS[month - 1] + " " + (year || "") };
};

// ---------- گرفتن داده ----------

/** چند درخواست همزمان — نه همه با هم، که سرور نخوابد. */
const CONCURRENCY = 5;

/*
  کش اسکنِ کاملِ جدول.

  lookupBaker در بدترین حالت دو بار داده می‌گیرد: یک بار با نام، و
  اگر جست‌وجوی سرور چیزی نداد، یک بار کلِ جدول. آن اسکن کامل ۱۰
  صفحه است و قبلاً *یکی‌یکی* گرفته می‌شد — روی هر جست‌وجوی ناموفق،
  چند ثانیه هنگ. حالا هم همزمان گرفته می‌شود و هم برای بقیه‌ی نشست
  کش می‌ماند.
*/
let fullScan: Promise<QuotaRow[]> | null = null;

const requestPage = async (page: number, search: string) => {
    const res = await Api.get<any>(ENDPOINT, {
        params: {
            page,
            size: API_PAGE_SIZE,
            ...(search ? { search } : {}),
        },
    });
    return {
        items: (res.data?.items || []) as QuotaRow[],
        pages: Number(res.data?.pages || 0),
    };
};

const fetchRowsUncached = async (search: string): Promise<QuotaRow[]> => {
    const first = await requestPage(1, search);
    const collected: QuotaRow[] = first.items.slice();

    const lastPage = Math.min(first.pages || 1, MAX_PAGES);
    const remaining: number[] = [];
    for (let page = 2; page <= lastPage; page++) remaining.push(page);

    for (let i = 0; i < remaining.length; i += CONCURRENCY) {
        const batch = remaining.slice(i, i + CONCURRENCY);
        const responses = await Promise.all(batch.map((page) => requestPage(page, search)));
        responses.forEach((response) => collected.push.apply(collected, response.items));
    }

    return collected;
};

const fetchRows = async (search: string): Promise<QuotaRow[]> => {
    if (search) return fetchRowsUncached(search);

    // اسکن کامل یک بار در هر نشست
    if (!fullScan) {
        fullScan = fetchRowsUncached("").catch((error) => {
            fullScan = null; // شکست را کش نمی‌کنیم
            throw error;
        });
    }
    return fullScan;
};

/** برای وقتی که سهمیه‌ها عوض شده‌اند و باید دوباره خوانده شوند. */
export const clearQuotaCache = (): void => {
    fullScan = null;
};

/**
 * ردیف‌های خام را به خبازی‌ها گروه می‌کند.
 *
 * کلید، کد خریدار است نه نام: دو خبازی هم‌نام در دو شهر وجود دارند
 * و با گروه کردن روی نام، سهمیه‌شان قاطی می‌شد.
 */
export const groupByBaker = (rows: QuotaRow[]): BakerQuota[] => {
    const byCode: Record<string, BakerQuota> = {};

    rows.forEach((row) => {
        const code = String(row.buyer_code || "").trim() || String(row.buyer_name || "");
        const parsed = parsePeriod(row.distribution_period || "");

        if (!byCode[code]) {
            byCode[code] = {
                name: String(row.buyer_name || "").trim(),
                code: String(row.buyer_code || "").trim(),
                city: String(row.city || "").trim(),
                bakingType: String(row.baking_type || "").trim(),
                months: [],
            };
        }

        byCode[code].months.push({
            period: row.distribution_period,
            label: parsed ? parsed.label : row.distribution_period,
            year: parsed ? parsed.year : 0,
            month: parsed ? parsed.month : 0,
            quota: Number(row.quota || 0),
        });
    });

    return Object.keys(byCode).map((code) => {
        const baker = byCode[code];

        // قدیمی‌ترین ماه اول — روایت «از خرداد تا مهر» طبیعی‌تر از برعکسش است
        baker.months.sort((a, b) => (a.year - b.year) || (a.month - b.month));

        baker.months.forEach((month, index) => {
            if (index === 0) return;
            month.delta = Math.round((month.quota - baker.months[index - 1].quota) * 100) / 100;
        });

        return baker;
    });
};

// ---------- پیدا کردن خبازی ----------

export interface QuotaLookup {
    kind: "found" | "many" | "none";
    /** kind === "found" */
    baker?: BakerQuota;
    /** kind === "many" — چند خبازی با این نام */
    matches?: BakerQuota[];
    /** kind === "none" — نزدیک‌ترین نام‌ها */
    suggestions?: string[];
}

/** آیا همه‌ی واژه‌های پرسش در نام هست؟ («جهانگیر» در «جهانگیر علی نیا») */
const nameContains = (name: string, query: string): boolean => {
    const target = normalize(name);
    const words = normalize(query).split(" ").filter(Boolean);
    if (!words.length) return false;
    return words.every((word) => target.indexOf(word) !== -1);
};

/**
 * نزدیک‌ترین نام‌ها وقتی هیچ تطبیق دقیقی نیست.
 *
 * معیار ساده و عمدی: اشتراک حرف‌به‌حرف از ابتدای واژه. تشخیص گفتار
 * معمولاً حرف‌های اول را درست می‌گیرد و آخر کلمه را خراب می‌کند
 * («جهانگیر» → «جهان گیر» یا «جهانگر»).
 */
const sharedPrefix = (a: string, b: string): number => {
    let i = 0;
    while (i < a.length && i < b.length && a[i] === b[i]) i++;
    return i;
};

export const suggestNames = (rows: QuotaRow[], query: string): string[] => {
    const words = normalize(query).split(" ").filter(Boolean);
    if (!words.length) return [];

    const scored: Array<{ name: string; score: number }> = [];
    const seen: Record<string, boolean> = {};

    rows.forEach((row) => {
        const name = String(row.buyer_name || "").trim();
        if (!name || seen[name]) return;
        seen[name] = true;

        const parts = normalize(name).split(" ").filter(Boolean);
        let best = 0;
        words.forEach((word) => {
            parts.forEach((part) => {
                const shared = sharedPrefix(part, word);
                // کمتر از ۳ حرف مشترک، شباهت نیست — تصادف است
                if (shared >= 3 && shared > best) best = shared;
            });
        });

        if (best) scored.push({ name, score: best });
    });

    return scored
        .sort((a, b) => b.score - a.score)
        .slice(0, 4)
        .map((item) => item.name);
};

/**
 * خبازی را از روی چیزی که کاربر گفت پیدا می‌کند.
 *
 * اول با جست‌وجوی سمت سرور، و اگر نشد با یک اسکن محلی — چون
 * جست‌وجوی سرور روی نام‌های چندبخشی و فاصله‌ی اضافه گاهی خالی
 * برمی‌گردد و در ورودی صوتی این خیلی پیش می‌آید.
 */
export const lookupBaker = async (query: string): Promise<QuotaLookup> => {
    const term = String(query || "").trim();
    if (!term) return { kind: "none", suggestions: [] };

    let rows = await fetchRows(term);

    // سرور چیزی نداد یا نامِ برگشته ربطی نداشت → خودمان می‌گردیم
    let bakers = groupByBaker(rows).filter((baker) => nameContains(baker.name, term));

    if (!bakers.length) {
        const all = await fetchRows("");
        rows = all;
        bakers = groupByBaker(all).filter((baker) => nameContains(baker.name, term));

        if (!bakers.length) {
            return { kind: "none", suggestions: suggestNames(all, term) };
        }
    }

    if (bakers.length > 1) return { kind: "many", matches: bakers };
    return { kind: "found", baker: bakers[0] };
};

// ---------- روایت ----------

const fa = (value: number | string): string => toPersianDigits(String(value));

/** «۳.۳ کیسه» */
const amount = (value: number): string => fa(Number(value).toFixed(1)) + " " + QUOTA_UNIT;

/**
 * متن پاسخ برای یک خبازی — همین متن هم خوانده می‌شود هم نوشته.
 *
 * عمداً جمله‌ای است نه جدولی: این پاسخ قرار است با صدا شنیده شود و
 * جدول با گوش فهمیده نمی‌شود.
 */
export const describeBaker = (baker: BakerQuota): string => {
    const head =
        "سهمیه‌ی " + baker.name +
        (baker.code ? "، کد " + toPersianDigits(baker.code) : "") +
        (baker.city ? "، شهرستان " + baker.city : "") +
        (baker.bakingType ? "، نان " + baker.bakingType : "") +
        ":";

    if (!baker.months.length) return head + " برای هیچ دوره‌ای سهمیه‌ای ثبت نشده است.";

    const lines = baker.months.map((month) => {
        const base = month.label + ": " + amount(month.quota);
        if (month.delta === undefined) return base;
        if (month.delta === 0) return base + " (بدون تغییر)";
        const word = month.delta > 0 ? "بیشتر" : "کمتر";
        return base + " (" + fa(Math.abs(month.delta).toFixed(1)) + " " + word + " از ماه قبل)";
    });

    return head + "\n" + lines.join("\n") + "\n" + summarize(baker);
};

/** یک جمله درباره‌ی کل روند — همان چیزی که کاربر واقعاً می‌خواهد بداند. */
export const summarize = (baker: BakerQuota): string => {
    const months = baker.months;
    if (months.length < 2) {
        return months.length === 1
            ? "فقط برای همین یک دوره سهمیه ثبت شده است."
            : "";
    }

    const first = months[0];
    const last = months[months.length - 1];
    const change = Math.round((last.quota - first.quota) * 100) / 100;

    const ups = months.filter((m) => (m.delta || 0) > 0).length;
    const downs = months.filter((m) => (m.delta || 0) < 0).length;

    const total = months.reduce((sum, m) => sum + m.quota, 0);
    const average = Math.round((total / months.length) * 100) / 100;

    const direction =
        change > 0
            ? "در مجموع " + fa(Math.abs(change).toFixed(1)) + " " + QUOTA_UNIT + " بیشتر شده"
            : change < 0
            ? "در مجموع " + fa(Math.abs(change).toFixed(1)) + " " + QUOTA_UNIT + " کمتر شده"
            : "در مجموع تغییری نکرده";

    return (
        "جمع‌بندی: از " + first.label + " تا " + last.label + " " + direction +
        "؛ " + fa(ups) + " بار افزایش و " + fa(downs) + " بار کاهش داشته. " +
        "میانگین ماهانه " + amount(average) + " است."
    );
};

/** سهمیه‌ی یک ماه مشخص، اگر کاربر ماه را نام برده باشد. */
export const monthOf = (baker: BakerQuota, monthName: string): MonthQuota | null => {
    const wanted = normalize(monthName);
    if (!wanted) return null;

    const found = baker.months.filter(
        (month) => wanted.indexOf(normalize(JALALI_MONTHS[month.month - 1] || "")) !== -1
    );
    // اگر کاربر ماه را گفته ولی سال را نه، تازه‌ترین سالِ همان ماه
    return found.length ? found[found.length - 1] : null;
};

/** آیا در جمله نام ماهی آمده است؟ */
export const monthNameIn = (sentence: string): string => {
    const text = normalize(sentence);
    for (let i = 0; i < JALALI_MONTHS.length; i++) {
        if (text.indexOf(normalize(JALALI_MONTHS[i])) !== -1) return JALALI_MONTHS[i];
    }
    return "";
};
